import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { ProductSlidersRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import ProductTileComponent from "@/components/Product/ProductTile/productTile.component";
import { ProductTileLayout } from "@/components/Product/ProductTile/productTile.config";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useProductCartContext } from "@/utils/hooks/cart.hook";
import { getEffectiveLayoutSettings } from "@/utils/swiper";

import { useProductSliderContext } from "./productSlider.context";

import "./productSlider.scss";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { cn } from "@omni/utils/styles";

const VisibilityTypeToFilter = new Set([
    ProductVisibilityType.Offline,
    ProductVisibilityType.NotAvailable,
    ProductVisibilityType.TemporaryNotAvailable
]);

const isVisible = (product: Product<ProductFieldSet.ProductTile>) =>
    !VisibilityTypeToFilter.has(product.productVisibilityType);

export type SkuProductSliderProps = {
    products: Array<Product<ProductFieldSet.ProductTile>>;
    deleteFromHistory?: any;
    deleteRecentsHistory?: any;
};

const ProductSliderComponent = ({
    products,
    deleteFromHistory,
    deleteRecentsHistory
}: SkuProductSliderProps) => {
    const { t } = useGranite();
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(true);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);
    const cartContext = useProductCartContext();

    const {
        source,
        title,
        breakpointsConfig,
        productTileConfig,
        ssrLayoutClasses,
        analyticsContext
    } = useProductSliderContext();

    useEffect(() => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    }, [mainSwiper]);

    const updateButtons = () => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    };

    const productSourceIsRecent = ProductSliderSources.Recent === source;

    return (
        <div
            className={classnames(
                productSourceIsRecent && "productSlider-fullwidth pb-2"
            )}
        >
            <div
                className={classnames(
                    ssrLayoutClasses && ssrLayoutClasses,
                    productSourceIsRecent && "productSlider-recents",
                    productSourceIsRecent && "container p-md-4"
                )}
            >
                <div className="row m-0 d-md-flex flex-row mb-3 mb-md-0">
                    {title && (
                        <h2 className={classnames("mr-3 my-2 flex-shrink-1")}>
                            {title}
                        </h2>
                    )}
                    <div
                        className={classnames(
                            "d-flex align-items-center flex-wrap flex-grow-1 "
                        )}
                    >
                        {productSourceIsRecent && deleteRecentsHistory && (
                            <a
                                className="mt-1"
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    deleteRecentsHistory();
                                }}
                            >
                                {t("productSlider.link.deleteHistory")}
                            </a>
                        )}
                    </div>
                </div>
                <div
                    className={classnames(
                        "fullwidth__slider",
                        "d-flex align-items-center w-100",
                        productSourceIsRecent &&
                            !allowNext &&
                            !allowPrevious &&
                            "justify-content-center"
                    )}
                >
                    <SwiperNavButton
                        innerRef={navigationPrevRef}
                        className={cn("tw:hidden tw:md:block")}
                        disabled={!allowPrevious}
                        icon="left"
                    />
                    <Swiper
                        modules={[Navigation]}
                        onSwiper={setMainSwiper}
                        updateOnWindowResize={true}
                        resizeObserver={true}
                        onSlideChange={() => updateButtons()}
                        spaceBetween={productSourceIsRecent ? 7 : 20}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current
                        }}
                        breakpoints={getEffectiveLayoutSettings(
                            breakpointsConfig.layoutSettings
                        )}
                    >
                        {products
                            .filter(isVisible)
                            .map((product, index: number) => (
                                <SwiperSlide
                                    key={product.sku}
                                    className="productSlider-slide ssr-slide"
                                >
                                    <ProductTileComponent
                                        product={product}
                                        cart={cartContext?.cart}
                                        allowInstantAddToCart={
                                            !!cartContext?.allowInstantAddToCart
                                        }
                                        productTileConfig={{
                                            amountOfFeatures:
                                                productTileConfig.amountOfFeatures,
                                            imageRenditions:
                                                ProductSlidersRenditions,
                                            border: false,
                                            tilePadding:
                                                productTileConfig.useInsidePadding,
                                            components:
                                                ProductTileLayout.SLIDER(
                                                    productTileConfig.showButton,
                                                    productTileConfig.showCategoryLink,
                                                    productTileConfig.amountOfFeatures >
                                                        0
                                                )
                                        }}
                                        analyticsContext={
                                            analyticsContext && {
                                                ...analyticsContext,
                                                placement: index + 1
                                            }
                                        }
                                    >
                                        {productSourceIsRecent &&
                                            deleteFromHistory && (
                                                <Button
                                                    type="button"
                                                    test="productSlider-button-delete"
                                                    className="productSlider-deleteButton d-none d-lg-block"
                                                    color={"transparent"}
                                                    aria-label={t(
                                                        "accessibility.productSlider.delete"
                                                    )}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        deleteFromHistory(
                                                            product.sku
                                                        );
                                                    }}
                                                >
                                                    <i className="las la-times" />
                                                </Button>
                                            )}
                                    </ProductTileComponent>
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    <SwiperNavButton
                        innerRef={navigationNextRef}
                        className={cn("tw:hidden tw:md:block")}
                        disabled={!allowNext}
                        icon="right"
                    />
                </div>
            </div>
        </div>
    );
};

export { ProductSliderComponent };
